// The session guard, from the browser's side (proposal 6.1). The access scope is the one source
// for every gate; the SPA never decides membership itself, it reads the server's answer.
import type { QueryClient } from "@tanstack/react-query";
import { redirect } from "@tanstack/react-router";

import { isSessionExpired } from "../api/errors";
import { GoogleStart } from "../api/generated/zod";
import { accessScopeQuery, type AccessScope } from "../api/session";

// A path that starts with two slashes (or a slash and a backslash) is protocol-relative: the
// browser reads "//evil.example" as another origin.
const PROTOCOL_RELATIVE = /^\/[/\\]/;

/** A `redirect` value is followed only when it is a path on this origin. */
export function safeRedirectPath(value: string | undefined): string | undefined {
  if (value === undefined || !value.startsWith("/") || PROTOCOL_RELATIVE.test(value)) {
    return undefined;
  }
  try {
    const url = new URL(value, window.location.origin);
    if (url.origin !== window.location.origin) return undefined;
    // Checked again after parsing: dot segments and backslashes can make "/a/..//evil.example"
    // normalize to "//evil.example" on this origin.
    if (PROTOCOL_RELATIVE.test(url.pathname)) return undefined;
    return url.pathname + url.search + url.hash;
  } catch {
    return undefined;
  }
}

/**
 * Where to go after signing in, and the one rule every sign-in path follows: the `redirect` when
 * it is a safe path that the API's `GoogleStart` would accept too, else `/`.
 */
export function wayBack(redirect: string | undefined): string {
  const path = safeRedirectPath(redirect);
  return path !== undefined && GoogleStart.safeParse({ redirect: path }).success ? path : "/";
}

/**
 * The way back to a location, for `?redirect=`: its path and query, never its fragment. A
 * fragment can hold a credential, such as an invitation token, and a query string reaches logs.
 */
export function returnPath(location: { pathname: string; searchStr: string }): string {
  return location.pathname + location.searchStr;
}

/** The scope of the signed-in user; without a session, a redirect to sign-in that comes back. */
export async function requireSession(
  queryClient: QueryClient,
  location: { pathname: string; searchStr: string },
): Promise<AccessScope> {
  try {
    return await queryClient.ensureQueryData(accessScopeQuery());
  } catch (error) {
    if (isSessionExpired(error)) {
      throw redirect({ to: "/sign-in", search: { redirect: returnPath(location) } });
    }
    throw error;
  }
}

/** On the sign-in page: a user who is already signed in goes where they were headed. */
export async function redirectIfSignedIn(
  queryClient: QueryClient,
  target: string | undefined,
): Promise<void> {
  let signedIn = false;
  try {
    await queryClient.ensureQueryData(accessScopeQuery());
    signedIn = true;
  } catch {
    // Signed out, or the API is unreachable: either way the sign-in page shows.
  }
  if (signedIn) throw redirect({ href: wayBack(target) });
}
